import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { useCart } from "../context/CartContext";
import {
    Minus,
    Plus,
    ShieldCheck,
    PackageCheck,
    Truck,
    Lock,
    Star,
} from "lucide-react";
import ProductCard from "../components/ProductCard";

export default function ProductDetail() {
    const { slug } = useParams();
    const { addItem } = useCart();
    const [p, setP] = useState(null);
    const [qty, setQty] = useState(1);
    const [related, setRelated] = useState([]);
    const [img, setImg] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setImg(0);
        setQty(1);
        setP(null);
        api.get(`/products/${slug}`).then((r) => {
            if (cancelled) return;
            setP(r.data);
            api.get(`/products?category=${r.data.category_slug}`).then(
                (rr) => {
                    if (cancelled) return;
                    setRelated(
                        rr.data
                            .filter((x) => x.slug !== slug)
                            .slice(0, 4),
                    );
                },
            );
        });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (!p)
        return (
            <div
                className="max-w-[1440px] mx-auto px-6 md:px-12 py-40 text-center"
                style={{ color: "var(--vl-muted)" }}
            >
                Loading…
            </div>
        );

    const gallery = p.gallery && p.gallery.length > 0 ? p.gallery : [p.image];

    return (
        <div data-testid="product-detail-page">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-10">
                <div
                    className="overline"
                    style={{ color: "var(--vl-muted)" }}
                >
                    <Link to="/shop">Shop</Link>{" "}
                    <span className="mx-2">/</span>
                    <Link to={`/shop?category=${p.category_slug}`}>
                        {p.category}
                    </Link>
                </div>
            </div>

            <section className="max-w-[1440px] mx-auto px-6 md:px-12 pt-8 pb-16 grid lg:grid-cols-2 gap-10">
                <div>
                    <div
                        className="relative overflow-hidden"
                        style={{
                            background: "var(--vl-surface)",
                            aspectRatio: "4 / 5",
                        }}
                    >
                        <img
                            src={gallery[img]}
                            alt={p.name}
                            className="absolute inset-0 w-full h-full object-cover"
                            data-testid="product-main-image"
                        />
                    </div>
                    {gallery.length > 1 && (
                        <div className="flex gap-3 mt-4">
                            {gallery.map((g, i) => (
                                <button
                                    key={g}
                                    onClick={() => setImg(i)}
                                    data-testid={`product-thumb-${i}`}
                                    className="w-20 h-24 overflow-hidden"
                                    style={{
                                        border:
                                            img === i
                                                ? "1px solid var(--vl-rose)"
                                                : "1px solid var(--vl-border)",
                                    }}
                                >
                                    <img
                                        src={g}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div>
                    <div className="overline mb-3">{p.category}</div>
                    <h1
                        className="font-serif text-4xl sm:text-5xl tracking-tight"
                        style={{ color: "var(--vl-text)" }}
                        data-testid="product-title"
                    >
                        {p.name}
                    </h1>
                    <div className="flex items-center gap-4 mt-4">
                        <div
                            className="flex items-center gap-1"
                            style={{ color: "var(--vl-rose)" }}
                        >
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Star
                                    key={`star-${i}`}
                                    size={14}
                                    fill={
                                        i < Math.round(p.rating)
                                            ? "currentColor"
                                            : "transparent"
                                    }
                                    strokeWidth={1.25}
                                />
                            ))}
                        </div>
                        <span
                            className="text-xs"
                            style={{ color: "var(--vl-muted)" }}
                        >
                            {p.rating} · {p.reviews_count} private reviews
                        </span>
                    </div>

                    <div className="mt-6 flex items-baseline gap-3">
                        <div
                            className="font-serif text-3xl"
                            style={{ color: "var(--vl-text)" }}
                            data-testid="product-detail-price"
                        >
                            {formatINR(p.price)}
                        </div>
                        {p.compare_at_price && (
                            <div
                                className="line-through text-sm"
                                style={{ color: "var(--vl-muted)" }}
                            >
                                {formatINR(p.compare_at_price)}
                            </div>
                        )}
                    </div>

                    <p
                        className="mt-6 text-base"
                        style={{
                            color: "var(--vl-muted)",
                            lineHeight: 1.9,
                        }}
                        data-testid="product-description"
                    >
                        {p.description}
                    </p>

                    <div className="mt-8 flex items-center gap-4">
                        <div
                            className="flex items-center gap-3"
                            style={{ border: "1px solid var(--vl-border)" }}
                        >
                            <button
                                onClick={() => setQty(Math.max(1, qty - 1))}
                                data-testid="pdp-qty-dec"
                                className="w-10 h-12 flex items-center justify-center"
                            >
                                <Minus
                                    size={14}
                                    color="var(--vl-text)"
                                    strokeWidth={1.25}
                                />
                            </button>
                            <span
                                className="text-sm w-5 text-center"
                                style={{ color: "var(--vl-text)" }}
                            >
                                {qty}
                            </span>
                            <button
                                onClick={() => setQty(qty + 1)}
                                data-testid="pdp-qty-inc"
                                className="w-10 h-12 flex items-center justify-center"
                            >
                                <Plus
                                    size={14}
                                    color="var(--vl-text)"
                                    strokeWidth={1.25}
                                />
                            </button>
                        </div>
                        <button
                            onClick={() => addItem(p, qty)}
                            data-testid="pdp-add-to-bag"
                            className="vl-btn vl-btn-solid flex-1 justify-center"
                        >
                            Add to bag — {formatINR(p.price * qty)}
                        </button>
                    </div>

                    <div
                        className="grid sm:grid-cols-2 gap-3 mt-10 pt-10"
                        style={{ borderTop: "1px solid var(--vl-border)" }}
                    >
                        {[
                            {
                                icon: <ShieldCheck size={16} strokeWidth={1.25} />,
                                t: "Discreet packaging",
                            },
                            {
                                icon: <PackageCheck size={16} strokeWidth={1.25} />,
                                t: "Dermatologist-tested",
                            },
                            {
                                icon: <Truck size={16} strokeWidth={1.25} />,
                                t: "COD available in India",
                            },
                            {
                                icon: <Lock size={16} strokeWidth={1.25} />,
                                t: "Private checkout",
                            },
                        ].map((f) => (
                            <div
                                key={f.t}
                                className="flex items-center gap-3 text-sm"
                                style={{ color: "var(--vl-text)" }}
                            >
                                <span style={{ color: "var(--vl-rose)" }}>
                                    {f.icon}
                                </span>
                                {f.t}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {related.length > 0 && (
                <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-32">
                    <div className="overline mb-4">You may also love</div>
                    <h2
                        className="font-serif text-3xl sm:text-4xl tracking-tight mb-10"
                        style={{ color: "var(--vl-text)" }}
                    >
                        From the same collection.
                    </h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                        {related.map((r, i) => (
                            <ProductCard
                                key={r.id}
                                product={r}
                                index={i}
                            />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
